import { useState, useEffect, useMemo } from 'react';
import { Plus, Edit, Trash2, X, Save, Warehouse, Loader2, MapPinned } from 'lucide-react';
import { warehouseService } from '../services/warehouseService';
import { customerService } from '../services/customerService';
import { AdvancedTable } from '../components/AdvancedTable';
import { exportRowsToExcel } from '../utils/excelExport';

interface WarehouseData {
  id: string;
  name: string;
  code: string;
  shipmentCode?: string;
  city: string;
  district: string;
  address: string;
  latitude?: number;
  longitude?: number;
  capacity: number;
  rampCount: number;
  contactPerson: string;
  phone: string;
  email: string;
  accountingContactPerson: string;
  accountingPhone: string;
  accountingEmail: string;
  invoiceCustomerId?: string;
  invoiceCustomerName?: string;
  workingHoursStart?: string;
  workingHoursEnd?: string;
  serviceDurationMinutes?: number;
  serviceHours?: Record<string, { start: string; end: string }>;
  enforceBasePriceForNewShipments?: boolean;
  basePriceEnforcementValidFrom?: string;
  status: 'active' | 'inactive';
  notes?: string;
}

const SERVICE_DAYS = [
  ['monday', 'Pazartesi'],
  ['tuesday', 'Salı'],
  ['wednesday', 'Çarşamba'],
  ['thursday', 'Perşembe'],
  ['friday', 'Cuma'],
  ['saturday', 'Cumartesi'],
  ['sunday', 'Pazar'],
] as const;

const defaultServiceHours = () => ({
  monday: { start: '08:00', end: '17:00' },
  tuesday: { start: '08:00', end: '17:00' },
  wednesday: { start: '08:00', end: '17:00' },
  thursday: { start: '08:00', end: '17:00' },
  friday: { start: '08:00', end: '17:00' },
  saturday: { start: '00:00', end: '00:00' },
  sunday: { start: '00:00', end: '00:00' },
});

const mapApiWarehouseToUi = (warehouse: any): WarehouseData => ({
  id: String(warehouse.id),
  name: warehouse.name || '',
  code: warehouse.code || '',
  shipmentCode: warehouse.shipment_code || '',
  city: warehouse.city || '',
  district: warehouse.district || '',
  address: warehouse.address || '',
  latitude: warehouse.latitude ? Number(warehouse.latitude) : undefined,
  longitude: warehouse.longitude ? Number(warehouse.longitude) : undefined,
  capacity: Number(warehouse.capacity_m3 || 0),
  rampCount: Number(warehouse.ramp_count || 0),
  contactPerson: warehouse.manager_name || '',
  phone: warehouse.manager_phone || '',
  email: warehouse.manager_email || '',
  accountingContactPerson: warehouse.accounting_contact_name || '',
  accountingPhone: warehouse.accounting_contact_phone || '',
  accountingEmail: warehouse.accounting_contact_email || '',
  invoiceCustomerId: warehouse.invoice_customer_id ? String(warehouse.invoice_customer_id) : '',
  invoiceCustomerName: warehouse.invoice_customer_name || '',
  workingHoursStart: warehouse.working_hours_start || '',
  workingHoursEnd: warehouse.working_hours_end || '',
  serviceDurationMinutes: Number(warehouse.service_duration_minutes || 60),
  enforceBasePriceForNewShipments: Number(warehouse.enforce_base_price_for_new_shipments || 0) === 1,
  basePriceEnforcementValidFrom: warehouse.base_price_enforcement_valid_from ? String(warehouse.base_price_enforcement_valid_from).slice(0, 10) : '',
  serviceHours: (() => {
    try {
      const parsed = typeof warehouse.service_hours_json === 'string' ? JSON.parse(warehouse.service_hours_json) : warehouse.service_hours_json;
      return parsed && typeof parsed === 'object' ? { ...defaultServiceHours(), ...parsed } : defaultServiceHours();
    } catch {
      return defaultServiceHours();
    }
  })(),
  status: warehouse.status || 'active',
  notes: warehouse.notes || '',
});

const mapUiWarehouseToApi = (warehouse: Partial<WarehouseData>) => ({
  name: warehouse.name,
  code: warehouse.code,
  shipment_code: warehouse.shipmentCode || null,
  city: warehouse.city,
  district: warehouse.district,
  address: warehouse.address,
  latitude: warehouse.latitude,
  longitude: warehouse.longitude,
  capacity_m3: warehouse.capacity,
  ramp_count: warehouse.rampCount,
  manager_name: warehouse.contactPerson,
  manager_phone: warehouse.phone,
  manager_email: warehouse.email,
  accounting_contact_name: warehouse.accountingContactPerson,
  accounting_contact_phone: warehouse.accountingPhone,
  accounting_contact_email: warehouse.accountingEmail,
  invoice_customer_id: warehouse.invoiceCustomerId || null,
  invoice_customer_name: warehouse.invoiceCustomerName || null,
  working_hours_start: warehouse.workingHoursStart || null,
  working_hours_end: warehouse.workingHoursEnd || null,
  service_duration_minutes: warehouse.serviceDurationMinutes || 60,
  enforce_base_price_for_new_shipments: warehouse.enforceBasePriceForNewShipments === true,
  base_price_enforcement_valid_from: warehouse.basePriceEnforcementValidFrom || null,
  service_hours_json: warehouse.serviceHours || defaultServiceHours(),
  status: warehouse.status,
  notes: warehouse.notes,
});

export function WarehouseSettings() {
  const [warehouses, setWarehouses] = useState<WarehouseData[]>([]);
  const [customers, setCustomers] = useState<Array<{ id: string; name: string }>>([]);
  const [stokbarLoading, setStokbarLoading] = useState(false);
  const [stokbarImporting, setStokbarImporting] = useState(false);
  const [showStokbarPreview, setShowStokbarPreview] = useState(false);
  const [stokbarPreviewRows, setStokbarPreviewRows] = useState<any[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<WarehouseData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<Partial<WarehouseData>>({
    status: 'active',
    serviceDurationMinutes: 60,
    serviceHours: defaultServiceHours(),
  });

  useEffect(() => {
    loadWarehouses();
    void customerService.getCustomers({ status: 'active' }).then((response: any) => {
      const rows = Array.isArray(response?.data) ? response.data : [];
      setCustomers(rows.map((item: any) => ({ id: String(item.id), name: item.company_name || item.name || `Müşteri #${item.id}` })));
    }).catch(() => setCustomers([]));
  }, []);

  const loadWarehouses = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await warehouseService.getWarehouses();
      if (response.success && response.data) {
        setWarehouses(response.data.map(mapApiWarehouseToUi));
      } else {
        setWarehouses([]);
      }
    } catch (err: any) {
      console.error('Error loading warehouses:', err);
      setError(err.message || 'Depolar yüklenirken hata oluştu');
      setWarehouses([]);
    } finally {
      setIsLoading(false);
    }
  };

  const saveWarehouse = async () => {
    if (!formData.name || !formData.code || !formData.city) {
      alert('Lütfen zorunlu alanları doldurun');
      return;
    }

    setIsSaving(true);
    try {
      if (editingItem) {
        const response = await warehouseService.updateWarehouse(editingItem.id, mapUiWarehouseToApi(formData));
        if (response.success) {
          await loadWarehouses();
        }
      } else {
        const response = await warehouseService.createWarehouse(mapUiWarehouseToApi(formData));
        if (response.success) {
          await loadWarehouses();
        }
      }
      closeForm();
    } catch (err: any) {
      console.error('Error saving warehouse:', err);
      alert(err.message || 'Kayıt sırasında hata oluştu');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteWarehouse = async (id: string) => {
    if (confirm('Bu depoyu silmek istediğinizden emin misiniz?')) {
      try {
        const response = await warehouseService.deleteWarehouse(id);
        if (response.success) {
          await loadWarehouses();
        }
      } catch (err: any) {
        console.error('Error deleting warehouse:', err);
        alert(err.message || 'Silme sırasında hata oluştu');
      }
    }
  };

  const openForm = (warehouse?: WarehouseData) => {
    if (warehouse) {
      setEditingItem(warehouse);
      setFormData(warehouse);
    } else {
        setEditingItem(null);
        setFormData({ status: 'active', rampCount: 0, capacity: 0, accountingContactPerson: '', accountingPhone: '', accountingEmail: '', serviceDurationMinutes: 60, serviceHours: defaultServiceHours() });
    }
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingItem(null);
      setFormData({ status: 'active', accountingContactPerson: '', accountingPhone: '', accountingEmail: '', serviceDurationMinutes: 60, serviceHours: defaultServiceHours() });
  };

  const openWarehouseLocation = (warehouse: WarehouseData) => {
    if (warehouse.latitude == null || warehouse.longitude == null) return;
    window.open(`https://www.google.com/maps?q=${warehouse.latitude},${warehouse.longitude}`, '_blank', 'noopener,noreferrer');
  };

  const handlePreviewStokbarWarehouses = async () => {
    try {
      setStokbarLoading(true);
      const response: any = await warehouseService.previewTransferFromStokbar();
      if (!response?.success) throw new Error(response?.message || 'Stokbar depo önizlemesi alınamadı');
      setStokbarPreviewRows(Array.isArray(response.data?.rows) ? response.data.rows : []);
      setShowStokbarPreview(true);
    } catch (error: any) {
      console.error('Warehouse Stokbar preview failed:', error);
      alert(error?.message || 'Stokbar depo önizlemesi alınamadı');
    } finally {
      setStokbarLoading(false);
    }
  };

  const handleImportStokbarWarehouses = async () => {
    try {
      setStokbarImporting(true);
      const readyRows = stokbarPreviewRows.filter((row) => row.status === 'ready');
      const response: any = await warehouseService.transferFromStokbar(readyRows);
      if (!response?.success) throw new Error(response?.message || 'Stokbar depoları aktarılamadı');
      await loadWarehouses();
      setShowStokbarPreview(false);
      setStokbarPreviewRows([]);
    } catch (error: any) {
      console.error('Warehouse Stokbar import failed:', error);
      alert(error?.message || 'Stokbar depoları aktarılamadı');
    } finally {
      setStokbarImporting(false);
    }
  };

  const tableData = useMemo(() => warehouses.map((warehouse) => ({
    id: warehouse.id,
    name: warehouse.name || '-',
    code: warehouse.code || '-',
    shipmentCode: warehouse.shipmentCode || '-',
    city: warehouse.city || '-',
    district: warehouse.district || '-',
    address: warehouse.address || '-',
    latitude: warehouse.latitude != null ? Number(warehouse.latitude).toFixed(6) : '-',
    longitude: warehouse.longitude != null ? Number(warehouse.longitude).toFixed(6) : '-',
    capacity: `${warehouse.capacity || 0} m3`,
    rampCount: warehouse.rampCount || 0,
    contactPerson: warehouse.contactPerson || '-',
    phone: warehouse.phone || '-',
    email: warehouse.email || '-',
    accountingContactPerson: warehouse.accountingContactPerson || '-',
    accountingPhone: warehouse.accountingPhone || '-',
    accountingEmail: warehouse.accountingEmail || '-',
    invoiceCustomerName: warehouse.invoiceCustomerName || '-',
    workingHours: warehouse.workingHoursStart && warehouse.workingHoursEnd ? `${warehouse.workingHoursStart} - ${warehouse.workingHoursEnd}` : '-',
    serviceDuration: `${warehouse.serviceDurationMinutes || 60} dk`,
    status: warehouse.status === 'active' ? 'Aktif' : 'Pasif',
    notes: warehouse.notes || '-',
  })), [warehouses]);

  const tableColumns = useMemo(() => [
    { key: 'name', label: 'Depo Adı' },
    { key: 'code', label: 'Kod' },
    { key: 'shipmentCode', label: 'Sevk Kısa Kod' },
    { key: 'city', label: 'İl' },
    { key: 'district', label: 'İlçe' },
    { key: 'address', label: 'Adres' },
    { key: 'latitude', label: 'Enlem' },
    { key: 'longitude', label: 'Boylam' },
    { key: 'capacity', label: 'Kapasite' },
    { key: 'rampCount', label: 'Rampa' },
    { key: 'contactPerson', label: 'Sorumlu' },
    { key: 'phone', label: 'Telefon' },
    { key: 'email', label: 'E-posta' },
    { key: 'accountingContactPerson', label: 'Muhasebe Sorumlu' },
    { key: 'accountingPhone', label: 'Muhasebe Telefon' },
    { key: 'accountingEmail', label: 'Muhasebe E-posta' },
    { key: 'invoiceCustomerName', label: 'Sabit Fatura Müşterisi' },
    { key: 'workingHours', label: 'Çalışma Saatleri' },
    { key: 'serviceDuration', label: 'Servis Süresi' },
    {
      key: 'status',
      label: 'Durum',
      render: (value: string) => (
        <span className={`px-2 py-1 rounded text-xs font-medium ${value === 'Aktif' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
          {value}
        </span>
      ),
    },
    { key: 'notes', label: 'Notlar' },
    {
      key: 'id',
      label: 'İşlem',
      render: (_value: string, record: any) => {
        const warehouse = warehouses.find((item) => item.id === String(record.id));
        if (!warehouse) return null;
        return (
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); openWarehouseLocation(warehouse); }}
              disabled={warehouse.latitude == null || warehouse.longitude == null}
              className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-emerald-600 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <MapPinned className="w-4 h-4" />
              Konuma Git
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); openForm(warehouse); }}
              className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
            >
              <Edit className="w-4 h-4" />
              Düzenle
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); void deleteWarehouse(warehouse.id); }}
              className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
            >
              <Trash2 className="w-4 h-4" />
              Sil
            </button>
          </div>
        );
      },
    },
  ], [warehouses]);

  const handleExportExcel = () => exportRowsToExcel(tableData as any[], `warehouse-settings-${new Date().toISOString().slice(0, 10)}.xlsx`, { sheetName: 'Warehouses' });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Depo Yönetimi</h1>
          <p className="text-gray-500 mt-1">Depoları tanımlayın ve yönetin</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleExportExcel}
            className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Excele Aktar
          </button>
          <button
            onClick={handlePreviewStokbarWarehouses}
            disabled={stokbarLoading}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {stokbarLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
            Stokbar'dan Aktar
          </button>
          <button
            onClick={() => openForm()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5" />
            Yeni Depo Ekle
          </button>
        </div>
      </div>

      {/* Loading/Error States */}
      {isLoading && (
        <div className="flex items-center justify-center py-12 bg-white rounded-lg border border-gray-200">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            <p className="text-gray-500">Depolar yükleniyor...</p>
          </div>
        </div>
      )}

      {error && !isLoading && (
        <div className="flex items-center justify-center py-12 bg-white rounded-lg border border-red-200">
          <div className="flex flex-col items-center gap-3 p-8">
            <p className="text-red-600 font-medium">{error}</p>
            <button
              onClick={loadWarehouses}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Tekrar Dene
            </button>
          </div>
        </div>
      )}

      {/* Warehouse Table */}
      {!isLoading && !error && (
        <>
          <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
            <div className="flex flex-col gap-3 border-b border-gray-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Depo Listesi</h2>
                <p className="text-sm text-gray-500 mt-1">Tüm depo alanları advanced tablo görünümünde listelenir</p>
              </div>
              <div className="text-sm font-medium text-gray-500">{warehouses.length} kayıt</div>
            </div>

            <AdvancedTable data={tableData as any} columns={tableColumns as any} disablePagination fixedTableHeight={500} onRefresh={loadWarehouses} onRowDoubleClick={(record: any) => {
              const warehouse = warehouses.find((item) => item.id === String(record.id));
              if (warehouse) openForm(warehouse);
            }} />
          </div>

          {warehouses.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <Warehouse className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Henüz depo eklenmemiş</p>
            </div>
          )}
        </>
      )}

      {/* Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 flex flex-col gap-3 border-b border-gray-200 bg-white p-6 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {editingItem ? 'Depo Düzenle' : 'Yeni Depo Ekle'}
              </h2>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Depo Adı *
                  </label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Aksaray Merkez Depo"
                  />
                </div>

                <label className="col-span-2 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <input type="checkbox" checked={Boolean(formData.enforceBasePriceForNewShipments)} onChange={(e) => setFormData({ ...formData, enforceBasePriceForNewShipments: e.target.checked })} className="mt-0.5 h-4 w-4" />
                  <span><strong>Yeni siparişlerde baz fiyat dışı navlunu engelle</strong><br />Bu depodan açılan yeni siparişlerde anlaşma/navlun bedeli hedef fiyattan farklı girilemez.</span>
                </label>
                <div className="col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700">Baz Fiyat Kısıtı Geçerlilik Tarihi</label>
                  <input type="date" value={formData.basePriceEnforcementValidFrom || ''} onChange={(e) => setFormData({ ...formData, basePriceEnforcementValidFrom: e.target.value })} className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:ring-2 focus:ring-blue-500" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Depo Kodu *
                  </label>
                  <input
                    type="text"
                    value={formData.code || ''}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="AKS-01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sevk Kısa Kod
                  </label>
                  <input
                    type="text"
                    value={formData.shipmentCode || ''}
                    onChange={(e) => setFormData({ ...formData, shipmentCode: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="PE"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    İl *
                  </label>
                  <input
                    type="text"
                    value={formData.city || ''}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Aksaray"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    İlçe
                  </label>
                  <input
                    type="text"
                    value={formData.district || ''}
                    onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Merkez"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adres
                  </label>
                  <textarea
                    value={formData.address || ''}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Organize Sanayi Bölgesi"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kapasite (m³)
                  </label>
                  <input
                    type="number"
                    value={formData.capacity || ''}
                    onChange={(e) => setFormData({ ...formData, capacity: Number(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="5000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rampa Sayısı
                  </label>
                  <input
                    type="number"
                    value={formData.rampCount || ''}
                    onChange={(e) => setFormData({ ...formData, rampCount: Number(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="8"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sorumlu Kişi
                  </label>
                  <input
                    type="text"
                    value={formData.contactPerson || ''}
                    onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Mehmet Demir"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    value={formData.phone || ''}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="0382 123 45 67"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    E-posta
                  </label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="aksaray@yukortagi.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Muhasebe Sorumlusu
                  </label>
                  <input
                    type="text"
                    value={formData.accountingContactPerson || ''}
                    onChange={(e) => setFormData({ ...formData, accountingContactPerson: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Ayşe Yılmaz"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Muhasebe Telefon
                  </label>
                  <input
                    type="tel"
                    value={formData.accountingPhone || ''}
                    onChange={(e) => setFormData({ ...formData, accountingPhone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="0382 123 45 68"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Muhasebe E-posta
                  </label>
                  <input
                    type="email"
                    value={formData.accountingEmail || ''}
                    onChange={(e) => setFormData({ ...formData, accountingEmail: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="muhasebe.aksaray@yukortagi.com"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sabit Fatura Müşterisi
                  </label>
                  <select
                    value={formData.invoiceCustomerId || ''}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const selectedCustomer = customers.find((item) => item.id === selectedId);
                      setFormData({
                        ...formData,
                        invoiceCustomerId: selectedId,
                        invoiceCustomerName: selectedCustomer?.name || '',
                      });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Seçili değil</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>{customer.name}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">Bu depo yükleme yeri seçildiğinde toplu siparişte fatura başlığı olarak bu müşteri kullanılır.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Başlangıç Saati
                  </label>
                  <input
                    type="time"
                    value={formData.workingHoursStart || ''}
                    onChange={(e) => setFormData({ ...formData, workingHoursStart: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bitiş Saati
                  </label>
                  <input
                    type="time"
                    value={formData.workingHoursEnd || ''}
                    onChange={(e) => setFormData({ ...formData, workingHoursEnd: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Servis Süresi (Dakika)
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={formData.serviceDurationMinutes || 60}
                    onChange={(e) => setFormData({ ...formData, serviceDurationMinutes: e.target.value ? Number(e.target.value) : 60 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="col-span-2 rounded-xl border border-gray-200 p-4">
                  <div className="mb-3 text-sm font-semibold text-gray-900">Kabul Zamanı</div>
                  <div className="space-y-3">
                    {SERVICE_DAYS.map(([key, label]) => (
                      <div key={key} className="grid grid-cols-[140px_1fr_1fr] items-center gap-3">
                        <div className="text-sm font-medium text-gray-700">{label}</div>
                        <input
                          type="time"
                          value={formData.serviceHours?.[key]?.start || '00:00'}
                          onChange={(e) => setFormData({
                            ...formData,
                            serviceHours: {
                              ...(formData.serviceHours || defaultServiceHours()),
                              [key]: {
                                ...(formData.serviceHours?.[key] || { start: '00:00', end: '00:00' }),
                                start: e.target.value,
                              },
                            },
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                          type="time"
                          value={formData.serviceHours?.[key]?.end || '00:00'}
                          onChange={(e) => setFormData({
                            ...formData,
                            serviceHours: {
                              ...(formData.serviceHours || defaultServiceHours()),
                              [key]: {
                                ...(formData.serviceHours?.[key] || { start: '00:00', end: '00:00' }),
                                end: e.target.value,
                              },
                            },
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enlem (Latitude)
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={formData.latitude || ''}
                    onChange={(e) => setFormData({ ...formData, latitude: e.target.value ? Number(e.target.value) : undefined })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="38.3687"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Boylam (Longitude)
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={formData.longitude || ''}
                    onChange={(e) => setFormData({ ...formData, longitude: e.target.value ? Number(e.target.value) : undefined })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="34.0390"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Durum
                  </label>
                  <select
                    value={formData.status || 'active'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Aktif</option>
                    <option value="inactive">Pasif</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notlar
                  </label>
                  <textarea
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Ek bilgiler"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={closeForm}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  İptal
                </button>
                <button
                  onClick={saveWarehouse}
                  disabled={isSaving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  <Save className="w-4 h-4" />
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showStokbarPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-col gap-3 border-b border-gray-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Stokbar Depo Önizleme</h2>
                <p className="text-sm text-gray-600 mt-1">`type=warehouse` servisi üzerinden bulunan depolar</p>
              </div>
              <button onClick={() => setShowStokbarPreview(false)} className="text-gray-400 hover:text-gray-600" disabled={stokbarImporting}><X className="w-6 h-6" /></button>
            </div>
            <div className="p-6 overflow-auto">
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left">Depo Kodu</th>
                      <th className="px-4 py-3 text-left">Depo Adı</th>
                      <th className="px-4 py-3 text-left">Lokasyon Kodu</th>
                      <th className="px-4 py-3 text-left">İşlem</th>
                      <th className="px-4 py-3 text-left">Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stokbarPreviewRows.map((row, index) => (
                      <tr key={`${row.code}-${row.name}-${index}`} className={row.status !== 'ready' ? 'bg-rose-50' : ''}>
                        <td className="px-4 py-3">{row.code || '-'}</td>
                        <td className="px-4 py-3">{row.name || '-'}</td>
                        <td className="px-4 py-3">{row.sourceCode || '-'}</td>
                        <td className="px-4 py-3">{row.importAction === 'update' ? 'Güncellenecek' : 'Yeni'}</td>
                        <td className="px-4 py-3">{row.status === 'ready' ? 'Hazır' : row.reason || 'Hata'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex flex-col gap-3 border-t border-gray-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-gray-500">Hazır satırlar depolara işlenecektir.</div>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowStokbarPreview(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50" disabled={stokbarImporting}>Kapat</button>
                <button onClick={handleImportStokbarWarehouses} disabled={stokbarImporting || !stokbarPreviewRows.some((row) => row.status === 'ready')} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                  {stokbarImporting ? 'Aktarılıyor...' : 'Aktar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
